/* eslint-disable react/forbid-prop-types */
import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { ThemeProvider, createGlobalStyle }  from 'styled-components';
import { Provider } from 'react-redux';
import { withRouter } from 'react-router-dom'
import get from 'lodash/get';
import Layout from './layout';
import configureStore, { history } from './redux/configureStore';
import initApp from './initApp';
import { resizeListener } from './utils';
import HealthCheck from './healthCheck';
import IdentityCheck from './identityCheck';
import { getMockQuestionSet, getNextQuestionSet } from './redux/actions';
import { getParams } from './util';
import { mockInactiveSessionResponse } from './mockResponse';
import useDocumentTitle from './hooks/useDocumentTitle';
import SessionStorageServices from './util/sessionStorageServices';
import useReactLogger from './config/newrelic';


const store = configureStore();

const GlobalStyle = createGlobalStyle`
  font-family: ${({ fontFamily }) => fontFamily};
  // Color contrast configs for SelectOption
  @media (forced-colors: active) {
	body {
		div[class$="menu"] > div > .app-select-option {
			background-color: #000;
			color: WindowText;
			forced-color-adjust: none;
			&[data-selected="true"] {
				background-color: WindowText;
				color: Highlight;
				&:hover {
					color: ButtonText;
				}
			}
			&:hover {
				background-color: Highlight;
				color: WindowText;
			}
		}
	}
  }
`

const Main = ({ config }) => {

	const { log: nrLog } = useReactLogger();

	const resizeHandler = useCallback(resizeListener.bind(window, store), []);

	const handleBrowserBackPress = () => {
		const uid = getParams('uid');
		const role = getParams('role')
		nrLog('AM-9017: popstate fired', {
			uid: uid || 'null',
			event: getParams('event') || 'null',
			href: window.location.href,
		});
		const params = { uid };
		if(role) {
			params.role = role;
		}
		store.dispatch(getNextQuestionSet('/questions', params))
	}

	const _handleBrowserBackPress = useCallback(handleBrowserBackPress.bind(window), []);

	useEffect(() => {
		initApp(store, config);
		window.addEventListener('resize', resizeHandler);
		window.addEventListener('popstate', _handleBrowserBackPress);

		const handlePageShow = (e) => {
			if (e.persisted) {
				nrLog('AM-9017: bfcache restore detected', {
					href: window.location.href,
					event: getParams('event') || 'null',
				});
				// Re-run initApp on bfcache restore
				initApp(store, config);
			}
		};
		window.addEventListener('pageshow', handlePageShow);

		const handleVisibility = () => {
			nrLog('AM-9017: visibilitychange', {
				state: document?.visibilityState ?? 'unknown',
				href: window?.location?.href ?? 'null',
				event: getParams('event') || 'null',
				envelopId: getParams('envelop_id') || 'null',
				uid: getParams('uid') || 'null',
			});
		};
		document.addEventListener('visibilitychange', handleVisibility);

		return () => {
			window.removeEventListener('resize', resizeHandler);
			window.removeEventListener('popstate', _handleBrowserBackPress);
			window.removeEventListener('pageshow', handlePageShow);
			document.removeEventListener('visibilitychange', handleVisibility);
		}
	}, []);

	const _getMockQuestionSet = useCallback(getMockQuestionSet(mockInactiveSessionResponse), [mockInactiveSessionResponse]);
	const _getNextQuestionSet = useCallback(getNextQuestionSet, []);
	const _getParams = useCallback(getParams, []);


	useEffect(() => {
		return history.listen(() => {
			const uid = _getParams('uid');
			const historyActions = ['PUSH', 'POP', 'REPLACE'];
			const identity = SessionStorageServices.getIdentity(uid);
			nrLog('AM-9017: history.listen fired', {
				action: history.action,
				uid: uid || 'null',
				identity: identity || 'null',
				href: window.location.href,
			});
			if (historyActions.indexOf(history.action) > -1) {
				if (identity === 'invalid') {
					nrLog('AM-9017: history.listen — identity invalid, blocking', { uid: uid || 'null' });
					store.dispatch(_getMockQuestionSet());
					return;
				}
			}
		  	if (history.action === 'POP') {
			  if(uid === null) {
				  // this covers the case when user clicks on browser back button
				  // at the landing page.
				  // https://sureify.atlassian.net/browse/NAT-1573
				nrLog('AM-9017: history.listen — POP with null uid, going back', {
					search: window?.location?.search ?? 'null',
					href: window?.location?.href ?? 'null',
				});
				window.history.back()
			  } else {
				nrLog('AM-9017: history.listen — POP with uid, dispatching /questions (no event)', {
					uid,
					urlEvent: _getParams('event') || 'null',
					urlEnvelopId: _getParams('envelop_id') || 'null',
					search: window?.location?.search ?? 'null',
					href: window?.location?.href ?? 'null',
				});
				store.dispatch(
					_getNextQuestionSet('/questions', {
						uid,
					}, {
						reload: true,
					}))
			  }
			}
		})
	  }, [history])

	const documentTitle = get(config, 'global.appTitle', 'Acquire')
	useDocumentTitle(documentTitle)
	
	return (
		<Provider store={ store }>
			{/* <Helmet>
				<title>
					{get(config, 'global.appTitle', 'Acquire')}
				</title>
			</Helmet> */}
			<HealthCheck>
				<ThemeProvider theme={ { config } }>
					<IdentityCheck>
						<GlobalStyle fontFamily={ get(config, 'theme.global.fontFamily', 'Proxima Nova') } />
						<Layout />
					</IdentityCheck>
				</ThemeProvider>
			</HealthCheck>
		</Provider>
	);
};

Main.propTypes = {
	config: PropTypes.any.isRequired,
};
export default withRouter(Main);
